import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import Loading from '@components/Loading';
import axios from '@helper/axiosInterceptor';
import { getApiPayload } from '@helper/apiError';
import { pageConstants } from '@helper/constants';
import { consumeQrVisit, getQrSession, isQrVisit, qrPath, qrVisitFromFragment, rememberQrToken, resolveQrToken, setQrBrowserSession } from '@helper/qrNavigation';
import { clearPendingQrEntry, isQrEntryNonce, pendingQrEntry } from '@helper/qrEntry';
import { QrHomeBootstrapContext } from '@helper/qrHomeBootstrap';
import { normalizeQrLocale } from '@languages/remoteCatalog';

const BrowserVisitGuard = ({ children }) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const fragmentVisit = qrVisitFromFragment(location.hash);
    const historyVisit = location.state?.orderproQrVisit;
    const includeHome = location.pathname.replace(/\/$/, '') === `${pageConstants.subFolder}/order`;
    const historyEntry = location.state?.orderproQrEntry;
    const entry = fragmentVisit !== null ? '' : isQrEntryNonce(historyEntry) ? historyEntry
        : !isQrVisit(historyVisit) && includeHome ? pendingQrEntry() : '';
    const tokenqr = entry ? '' : fragmentVisit !== null ? fragmentVisit
        : isQrVisit(historyVisit) ? historyVisit : resolveQrToken();
    const identity = entry ? `entry:${entry}` : `${tokenqr}:${includeHome ? 'home' : 'view'}`;
    const [proof, setProof] = useState({ identity: '', status: 'loading' });
    const [retryCount, setRetryCount] = useState(0);
    const acceptedEntry = useRef(null);
    const locale = useRef('ko');
    locale.current = normalizeQrLocale(i18n?.resolvedLanguage || i18n?.language || localStorage.getItem('lang'));

    useEffect(() => {
        if (entry) return;
        const fromEntry = qrVisitFromFragment(location.hash) !== null;
        if (fromEntry) consumeQrVisit(location.hash);
        if (!isQrVisit(tokenqr)) {
            if (fromEntry) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
            return;
        }
        rememberQrToken(tokenqr);
        // Each history entry keeps its own visit when another shop is opened.
        if (fromEntry || location.state?.orderproQrVisit !== tokenqr) {
            navigate(`${location.pathname}${location.search}${fromEntry ? '' : location.hash}`, {
                replace: true,
                state: { ...location.state, orderproQrVisit: tokenqr },
            });
        }
    }, [entry, tokenqr, location.hash, location.pathname, location.search, location.state, navigate]);

    useEffect(() => {
        const restore = event => {
            if (!event.persisted) return;
            // BFCache restores mounted pages without rerunning their effects.
            // Recheck this history entry before its controls use the active visit.
            acceptedEntry.current = null;
            setProof({ identity: '', status: 'loading' });
            setRetryCount(count => count + 1);
        };
        window.addEventListener('pageshow', restore);
        return () => window.removeEventListener('pageshow', restore);
    }, []);

    useEffect(() => {
        if (!entry && !isQrVisit(tokenqr)) {
            setProof({ identity, status: 'invalid' });
            return;
        }
        // Enter already returned proof and menu for this exact replacement route.
        if (acceptedEntry.current?.identity === identity) {
            setProof(acceptedEntry.current);
            acceptedEntry.current = null;
            return;
        }
        let active = true;
        const controller = new AbortController();
        const requestLocale = locale.current;
        const request = entry ? { action: 'enter', entry, include_home: true, l_code: requestLocale }
            : { action: 'view', visit: tokenqr, ...(includeHome ? { include_home: true, l_code: requestLocale } : {}) };
        setProof({ identity, status: 'loading' });
        axios.post('browser_session.php', request, {
            headers: { 'X-QR-Request': '1' }, withCredentials: true, timeout: 15000, signal: controller.signal,
        }).then(response => {
            if (!active) return;
            const result = getApiPayload(response);
            const visit = entry ? result.visit : tokenqr;
            if (includeHome || entry) {
                const home = result.home;
                if (home?.msg === 'SERVICE_INACTIVE' || home?.msg === 'expired_contract') {
                    if (entry) clearPendingQrEntry(entry);
                    setProof({ identity, status: home.msg === 'SERVICE_INACTIVE' ? 'service_inactive' : 'expired_contract' });
                    return;
                }
                const validMenu = home?.msg === 'ok' && home.tokenqr === visit && home.token === result.token
                    && ['event_list', 'item_category_list', 'item_list'].every(key => Array.isArray(home[key]));
                const closed = result.table_status === 'closed' && (home?.status === 'TABLE_CLOSED' || home?.msg === 'table_closed')
                    && typeof (home.message || home.validate) === 'string';
                if (!home || typeof home !== 'object' || Array.isArray(home) || (!validMenu && !closed)) {
                    throw new Error('invalid_qr_home_bootstrap');
                }
            }
            setQrBrowserSession(visit, result);
            const ready = { identity: entry ? `${visit}:home` : identity, visit, status: 'ready',
                tableStatus: result.table_status, locale: requestLocale, home: result.home };
            if (entry) {
                acceptedEntry.current = ready;
                clearPendingQrEntry(entry);
                const state = { ...location.state, orderproQrVisit: visit };
                delete state.orderproQrEntry;
                navigate(`${location.pathname}${location.search}`, { replace: true, state });
            }
            setProof(ready);
        }).catch(error => {
            if (!active) return;
            const payload = getApiPayload(error?.response?.data);
            const result = ['SERVICE_INACTIVE', 'expired_contract'].includes(payload.home?.msg) ? payload.home : payload;
            const status = result.msg === 'SERVICE_INACTIVE' ? 'service_inactive'
                : result.msg === 'expired_contract' ? 'expired_contract'
                : Number(error?.response?.status) === 403 ? 'invalid' : 'retry';
            if (entry && status !== 'retry') clearPendingQrEntry(entry);
            setProof({ identity, status });
        });
        return () => { active = false; controller.abort(); };
        // The request locale is a snapshot. Home refreshes its own locale changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entry, tokenqr, identity, includeHome, retryCount, navigate]);

    if (proof.identity === identity && proof.status === 'invalid') return <Navigate to={qrPath('')} replace />;
    if (proof.identity === identity && proof.status === 'service_inactive') return <Navigate to={`${pageConstants.subFolder}/service_inactive`} replace />;
    if (proof.identity === identity && proof.status === 'expired_contract') return <Navigate to={`${pageConstants.subFolder}/expired_contract`} replace />;
    if (proof.identity === identity && proof.status === 'retry') return <div className="nl-wrapper">
        <div className="qr-inline-error" role="alert">
            <p>{t('tr_validate.popup_alert_connect_error', 'Unable to connect. Please try again.')}</p>
            <button type="button" onClick={() => setRetryCount(count => count + 1)}>{t('tr_action.retry', 'Retry')}</button>
        </div>
    </div>;
    if (proof.identity !== identity || proof.status !== 'ready') return <Loading />;
    const home = qrPath(tokenqr, 'home');
    if (proof.tableStatus === 'closed' && location.pathname.replace(/\/$/, '') !== home) {
        return <Navigate to={home} replace state={{ ...location.state, orderproQrVisit: tokenqr }} />;
    }
    return <QrHomeBootstrapContext.Provider value={proof.home ? { visit: proof.visit, locale: proof.locale, home: proof.home } : null}>
        {children || <Loading />}
    </QrHomeBootstrapContext.Provider>;
};

const QrRouteGuard = ({ children }) => {
    const { tokenqr: routeToken } = useParams();
    if (!routeToken || isQrVisit(routeToken)) return <BrowserVisitGuard>{children}</BrowserVisitGuard>;
    const tokenqr = resolveQrToken(routeToken);
    if (!tokenqr) return <Navigate to={qrPath('', 'home')} replace />;
    if (!getQrSession(tokenqr)) {
        return <Navigate to={qrPath(tokenqr, 'home')} replace />;
    }
    return children || <Loading />;
};

export default QrRouteGuard;
